import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetDeviceListingQuery } from "../../../api/network/camerasAdminVMApiService";
import { useLazyGReverseGeocodeQuery } from "../../../api/network/mapApiServices";
import {
  downloadEventVideo,
  useDeleteSDCardFileMutation,
  useEventGetVideoDetailsMutation,
  useGetEventVideoDownloadUrlMutation,
} from "../../../api/network/vmApiService";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import {
  CameraEventGetVideoDetailResponse,
  FileRetrievalListingResponse,
} from "../../../api/types/Camera";
import svgIcons from "../../../assets/svg/svgIcons";
import { AdminFormFieldSubmit } from "../../../components/admin/formFields";
import { APP_CONFIG } from "../../../constants/constants";
import { useAccountTimezone } from "../../../hooks/timezone";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../../utils/dateTime";
import { CAMERA_CAPTURE_MODES } from "../constants";
import ModalDeleteFileConfirmation from "./modalDeleteFileConfirmation";
import ModalDownloadConfirmation from "./modalDownloadConfirmation";
import AppVideoElement from "../components/videoElement";

const ScreenVideoHistoryFileRetrievalDetails: FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });
  const location = useLocation();
  const deviceRecordingFromState =
    location?.state as FileRetrievalListingResponse;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const { tzForDateFns } = useAccountTimezone();
  const [videoAddress, setvideoAddress] = useState<string>("-");
  const [eventVideoDetails, setEventVideoDetails] =
    useState<CameraEventGetVideoDetailResponse | null>(null);
  const [
    eventGetVideoDetailsAPITrigger,
    {
      isLoading: isLoadinVideoDetails,
      isUninitialized: isUninitializedVideoDetails,
    },
  ] = useEventGetVideoDetailsMutation();

  const {
    data: dataDeviceListing,
    isFetching: isFetchingDeviceListing,
    error,
  } = useGetDeviceListingQuery(
    {
      keyword: deviceRecordingFromState?.cameraid,
      model: "",
      status: "",
      offset: 0,
      limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      linked_to_asset: 1,
    },
    {
      skip: !deviceRecordingFromState?.cameraid,
    }
  );

  const [downloadEventVideoApiTrigger, { isLoading: isDownloadingVideo }] =
    useGetEventVideoDownloadUrlMutation();
  const [deleteSDCardFileApiTrigger, { isLoading: isDeletingFile }] =
    useDeleteSDCardFileMutation();

  useEffect(() => {
    if (!!deviceRecordingFromState?.ticket && isUninitializedVideoDetails) {
      eventGetVideoDetailsAPITrigger({
        ticketId: deviceRecordingFromState?.ticket,
      })
        .unwrap()
        .then((res) => {
          if (!!res.data) setEventVideoDetails(res.data);
          else {
            if (APP_CONFIG.DEBUG.ALL)
              console.log("Failed to retrieve event video details");
          }
        })
        .catch((e) => {
          if (APP_CONFIG.DEBUG.ALL)
            console.log("Error while retrieving event video details");
        });
    }
  }, [deviceRecordingFromState?.ticket, isUninitializedVideoDetails]);

  const [gReverseGeocodeAPITrigger] = useLazyGReverseGeocodeQuery();
  useEffect(() => {
    (async () => {
      const lat = deviceRecordingFromState?.ft_lastvalidlatitude;
      const lng = deviceRecordingFromState?.ft_lastvalidlongitude;

      if (!lat || !lng) {
        setvideoAddress("");
        return;
      }

      try {
        const { results } = await gReverseGeocodeAPITrigger({
          latitude: parseFloat(lat),
          longitude: parseFloat(lng),
        }).unwrap();

        setvideoAddress(results?.[0]?.formatted_address ?? "");
      } catch (error) {
        console.error("Error fetching address:", error);
        setvideoAddress("");
      }
    })();
  }, [deviceRecordingFromState, gReverseGeocodeAPITrigger]);

  const ondownloadEventVideoClick = () => {
    if (!deviceRecordingFromState?.ticket) {
      console.error("No ticket available for downloading video");
      return;
    }
    dispatch(
      setModalsData({ ...modalsState, showEventDownloadConfirmation: true })
    );
  };

  const onDownloadConfirm = () => {
    const fileName = eventVideoDetails?.title?.split("_")[0] || "";

    downloadEventVideoApiTrigger({
      ticketId: eventVideoDetails?.ticket ?? "",
      fileName,
    })
      .then((res) => {
        if (res?.data?.download_url) {
          // Download the video file using the URL
          downloadEventVideo(res.data.download_url, fileName);
        } else {
          console.error("Failed to retrieve video download URL");
        }
      })
      .catch((error) => {
        console.error("Error downloading video:", error);
      });
  };

  const onDeleteFileClick = () => {
    dispatch(setModalsData({ ...modalsState, showModalDeleteFile: true }));
  };

  const onDeleteFileConfirm = () => {
    deleteSDCardFileApiTrigger({
      ticketId: eventVideoDetails?.ticket ?? "",
    })
      .then((res) => {
        if (res?.data?.result) {
          // File Deleted successfully(Take user back)
          toast.success(t("success_msg"));
          navigate(-1);
        } else {
          toast.error(t("error_msg"));
        }
      })
      .catch((error) => {
        console.error("Error downloading video:", error);
        toast.error(t("error_msg"));
      });
  };

  const formatDurations = (seconds?: number | null) => {
    if (!seconds || isNaN(seconds)) return "-";

    const totalSeconds = Math.floor(seconds);
    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const hh = hrs.toString().padStart(2, "0");
    const mm = mins.toString().padStart(2, "0");
    const ss = secs.toString().padStart(2, "0");

    return `${hh}:${mm}:${ss}`;
  };

  const VIDEO_DETAILS_INFORMATION = [
    {
      label: t("from"),
      value: eventVideoDetails?.video_start_timestamp
        ? formattedDateTimeBasedOnTimezone?.(
            dbTimeToJsTime(eventVideoDetails?.video_start_timestamp),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          )
        : "-",
    },
    {
      label: t("to"),
      value: eventVideoDetails?.video_end_timestamp
        ? formattedDateTimeBasedOnTimezone?.(
            dbTimeToJsTime(eventVideoDetails?.video_end_timestamp),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          )
        : "-",
    },
    {
      label: t("duration"),
      value: formatDurations(eventVideoDetails?.duration) || "-",
    },
  ];
  const RETRIEVAL_DETAILS_INFORMATION = [
    {
      label: t("camera_id"),
      value: deviceRecordingFromState?.cameraid || "-",
    },
    {
      label: t("asset_id"),
      value: dataDeviceListing?.data?.[0]?.ft_device_id || "-",
    },
    {
      label: t("description"),
      value: dataDeviceListing?.data?.[0]?.ft_device_description || "-",
    },
    {
      label: t("retrieve_time"),
      value: deviceRecordingFromState?.retrieve_time
        ? formattedDateTimeBasedOnTimezone?.(
            dbTimeToJsTime(deviceRecordingFromState?.retrieve_time),
            tzForDateFns,
            APP_CONFIG.DATETIME_FORMATS.DISPLAY.DATE_TIME
          )
        : "-",
    },
    // 6/17/2025 removed retrieve_by field as this will always be the same user
    // that we are using for making VisionMax API calls.
    // {
    //   label: t("retrieved_by"),
    //   value: deviceRecordingFromState?.retrieve_by || "-",
    // },
  ];

  return (
    <div className="p-4 bg-gray-100 rounded-lg flex flex-col gap-4">
      <header className="flex justify-between items-center pb-2">
        <div className="flex items-center">
          <img
            src={svgIcons.LeftArrowIcon}
            alt="left-arrow-icon"
            className="mr-4 p-2 bg-accent-blue-pale rounded-full cursor-pointer"
            onClick={() => {
              navigate(-1);
            }}
          />
          <h1 className="font-bold text-accent-blue-dark text-3xl leading-9">
            {t("retrieval_details")}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <AdminFormFieldSubmit
            variant="danger"
            type="button"
            label={
              isDeletingFile ? (
                <img
                  src={svgIcons.LoadingAnimation}
                  alt="Loading animation"
                  className={`size-5`}
                />
              ) : (
                "Delete"
              )
            }
            onClick={onDeleteFileClick}
          />
          <AdminFormFieldSubmit
            variant="primary"
            type="button"
            label={t("download")}
            onClick={ondownloadEventVideoClick}
            disabled={
              isDownloadingVideo || !eventVideoDetails?.videos?.[0]?.video_url
            }
          />
        </div>
      </header>
      {eventVideoDetails?.videos?.[0]?.video_url ? (
        <AppVideoElement
          controls
          muted
          playsInline
          poster={eventVideoDetails.videos[0].screenshot_url}
          videoAddress={videoAddress || "Address not available"}
          src={eventVideoDetails.videos[0].video_url}
          stretchPosterImage={
            !!eventVideoDetails?.videos?.[0]?.capture_mode &&
            [
              CAMERA_CAPTURE_MODES.grid, // combined video from 4TVI cameras with GRID
              CAMERA_CAPTURE_MODES.combined, // combined video from the front and in-cabin 2TVI cameras with S_F_I
            ].includes(
              `${eventVideoDetails?.videos?.[0]?.capture_mode}` as CAMERA_CAPTURE_MODES
            )
              ? true // fill to stretch the poster to align with the video properly
              : false
          }
          useVideoARForDimension={
            !!eventVideoDetails?.videos?.[0] &&
            !!eventVideoDetails?.videos?.[0].video_width &&
            !!eventVideoDetails?.videos?.[0].video_height
              ? eventVideoDetails?.videos?.[0]
              : undefined
          }
        />
      ) : (
        <div className="w-full h-96 flex items-center justify-center bg-gray-200 rounded-xl text-gray-500 text-lg">
          {t("no_video_available")}
        </div>
      )}
      <div className="bg-white rounded-lg p-6 space-y-6">
        <div className="col-span-12 space-y-4">
          <h1 className="block font-semibold text-base leading-none tracking-normal">
            {t("video_details")}
          </h1>
          <div className="col-span-12 grid grid-cols-12">
            {VIDEO_DETAILS_INFORMATION?.map((item, index) => {
              return (
                <div className="col-span-4" key={index}>
                  <div className="font-semibold text-base leading-6 tracking-normal text-accent-blue-dark">
                    {item.label}
                  </div>
                  <div className="font-medium text-sm leading-6 tracking-normal text-gray-400">
                    {item.value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="col-span-12 space-y-4">
          <h1 className="block font-semibold text-base leading-none tracking-normal">
            {t("retrieval_information")}
          </h1>
          <div className="col-span-12 grid grid-cols-12 space-y-2">
            {RETRIEVAL_DETAILS_INFORMATION?.map((item, index) => {
              return (
                <div
                  className={`${index % 2 === 0 || true ? "col-span-4" : "col-span-8"}`}
                  key={index}
                >
                  <div className="font-semibold text-base leading-6 tracking-normal text-accent-blue-dark">
                    {item.label}
                  </div>
                  <div className="font-medium text-sm leading-6 tracking-normal text-gray-400">
                    {item.value}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ModalDownloadConfirmation handleDownload={onDownloadConfirm} />
      <ModalDeleteFileConfirmation handleDeleteFile={onDeleteFileConfirm} />
    </div>
  );
};

export default ScreenVideoHistoryFileRetrievalDetails;
